import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjects } from '../services/projectService';
import ExecutiveRollup from '../components/dashboard/ExecutiveRollup';
import AdminDashboard from '../components/dashboard/AdminDashboard';
import StaffDashboard from '../components/dashboard/StaffDashboard';
import CommunityDashboard from '../components/dashboard/CommunityDashboard';
import ProponentDashboard from '../components/dashboard/ProponentDashboard';
import '../components/dashboard/AdminDashboard.css';

// ── Multi-community hub ───────────────────────────────────────────────────────
// Shown to 'user' panel members who belong to more than one community.
const ROLE_LABELS = {
  community_admin: 'Admin',
  community_staff: 'Staff',
  member:          'Member',
};

const MultiCommunityHub = ({ user, memberships }) => (
  <div className="page dashboard-page">
    <div className="page-header">
      <h1>My Communities</h1>
      <p>Welcome back, {user?.firstName || user?.email} — select a community to get started.</p>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, marginTop: 8 }}>
      {memberships.map((m) => {
        const tenant    = m.tenantId || {};
        const slug      = tenant.slug || m.tenantSlug;
        const name      = tenant.branding?.displayName || tenant.name || m.tenantName || slug;
        const color     = tenant.branding?.primaryColor || '#4285F4';
        const roleLabel = ROLE_LABELS[m.communityRole] || m.communityRole;
        return (
          <Link
            key={m._id || slug}
            to={`/t/${slug}/`}
            style={{
              display: 'block',
              textDecoration: 'none',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${color}`,
              borderRadius: 10,
              padding: '18px 20px',
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{name}</span>
              <span style={{
                fontSize: '0.72rem', fontWeight: 600,
                background: color + '22', color, borderRadius: 99, padding: '2px 8px',
              }}>{roleLabel}</span>
            </div>
            {tenant.description && (
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4 }}>
                {tenant.description}
              </p>
            )}
            <div style={{ marginTop: 12, fontSize: '0.82rem', color, fontWeight: 600 }}>
              Go to dashboard →
            </div>
          </Link>
        );
      })}
    </div>
  </div>
);

// ── Genpop onboarding panel ─────────────────────────────────────────────────
// Shown to authenticated users with no active community membership.
const GenpopOnboarding = ({ user }) => (
  <div className="page dashboard-page dashboard-page--genpop">
    <div className="page-header">
      <h1>Welcome, {user?.firstName || user?.email}</h1>
      <p>You're signed in to the Civic Intelligence Platform.</p>
    </div>

    <div className="onboarding-card">
      <h2>Get started</h2>
      <p>
        To participate in a community's consultations, news, and data tools,
        you need to be verified as a community member.
      </p>
      <Link to="/membership" className="btn btn--primary">
        Find or claim membership →
      </Link>
    </div>

    <div className="onboarding-card onboarding-card--secondary">
      <h3>Already submitted a claim?</h3>
      <p>
        Your claim is pending review by a community administrator.
        You'll gain access once it's approved.
        Check the <Link to="/membership">Membership page</Link> for status updates.
      </p>
    </div>
  </div>
);

// ── Platform-level overview (admin / staff) ──────────────────────────────────
const PlatformOverview = ({ user }) => (
  <div className="page dashboard-page dashboard-page--platform">
    <ExecutiveRollup user={user} />
  </div>
);

// ── Community / member project list ─────────────────────────────────────────
const ProjectList = ({ user, activeTenant }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (activeTenant?._id) {
      getProjects(activeTenant._id)
        .then((res) => setProjects(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [activeTenant]);

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back, {user?.firstName || user?.email}</p>
        {activeTenant?.branding?.displayName && (
          <span className="dashboard-community-label">{activeTenant.branding.displayName}</span>
        )}
      </div>

      <section>
        <div className="section-header">
          <h2>Projects</h2>
        </div>
        {loading ? (
          <p>Loading…</p>
        ) : projects.length === 0 ? (
          <p>No projects yet.</p>
        ) : (
          <ul className="item-list">
            {projects.map((p) => (
              <li key={p._id}>
                <Link to={`/projects/${p._id}`}>{p.title}</Link>
                <span className="badge">{p.type}</span>
                <span className="badge badge-gray">{p.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

// ── DashboardPage — panel-dispatch entry point ───────────────────────────────
const DashboardPage = () => {
  const { user, panel, activeTenant, memberships } = useAuth();

  if (panel === 'genpop')    return <GenpopOnboarding user={user} />;
  if (panel === 'admin')     return <AdminDashboard />;
  if (panel === 'staff')     return <StaffDashboard />;
  if (panel === 'community') return <CommunityDashboard />;
  if (panel === 'proponent') return <ProponentDashboard />;

  // 'user' panel — route based on how many active memberships exist.
  // Backend sets activeTenant only when there is exactly one active membership.
  if (!activeTenant && memberships?.length > 1) {
    return <MultiCommunityHub user={user} memberships={memberships} />;
  }
  if (!activeTenant) {
    return <GenpopOnboarding user={user} />;
  }
  return <ProjectList user={user} activeTenant={activeTenant} />;
};

export default DashboardPage;
